import {
  AVATAR_ALLOWED_TYPES,
  AVATAR_MAX_INPUT_BYTES,
  AVATAR_SIZE_PX
} from './types';

/**
 * Redimensiona una imagen a AVATAR_SIZE_PX × AVATAR_SIZE_PX y la exporta
 * como WebP comprimido. Sin dependencias externas: usa el API nativo del
 * navegador (createImageBitmap + canvas + convertToBlob).
 *
 * Si el navegador no soporta createImageBitmap o OffscreenCanvas, cae a
 * <img> + <canvas> clásico.
 */
export async function resizeToAvatar(file: File): Promise<Blob> {
  if (file.size > AVATAR_MAX_INPUT_BYTES) {
    throw new Error('too_large');
  }
  if (!AVATAR_ALLOWED_TYPES.includes(file.type)) {
    throw new Error('invalid_type');
  }

  const bitmap = await loadBitmap(file);
  const sw = bitmap.width;
  const sh = bitmap.height;

  if (sw === 0 || sh === 0) {
    if ('close' in bitmap && typeof bitmap.close === 'function') {
      bitmap.close();
    }
    throw new Error('resize_failed');
  }

  const side = Math.min(sw, sh);
  const sx = Math.floor((sw - side) / 2);
  const sy = Math.floor((sh - side) / 2);

  const canvas = document.createElement('canvas');
  canvas.width = AVATAR_SIZE_PX;
  canvas.height = AVATAR_SIZE_PX;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    if ('close' in bitmap && typeof bitmap.close === 'function') {
      bitmap.close();
    }
    throw new Error('resize_failed');
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    bitmap as CanvasImageSource,
    sx,
    sy,
    side,
    side,
    0,
    0,
    AVATAR_SIZE_PX,
    AVATAR_SIZE_PX
  );

  if ('close' in bitmap && typeof bitmap.close === 'function') {
    bitmap.close();
  }

  const blob = await canvasToWebp(canvas);
  if (!blob) throw new Error('resize_failed');
  return blob;
}

async function loadBitmap(
  file: File
): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file);
  }
  return loadHtmlImage(file);
}

function loadHtmlImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('resize_failed'));
    };
    img.src = url;
  });
}

async function canvasToWebp(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob),
      'image/webp',
      0.85
    );
  });
}

export function buildAvatarPath(userId: string): string {
  return `${userId}/avatar.webp`;
}